import { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import {
  useGetChargingStationsQuery,
  useCreateChargingStationMutation,
  useUpdateChargingStationMutation,
  useDeleteChargingStationMutation
} from '../../../redux/api/batteriesApi';
import { ChargingStation } from '../../../resources/types/batteriesTypes';
import { TableCell as MuiTableCell } from '@mui/material';

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:hover': {
    backgroundColor:
      theme.palette.mode === 'light'
        ? 'rgba(0, 0, 0, 0.04)'
        : 'rgba(255, 255, 255, 0.08)'
  },
  transition: 'background-color 0.2s ease'
}));

const StyledTableCell = styled(MuiTableCell)(({ theme }) => ({
  borderBottom: `1px solid ${
    theme.palette.mode === 'light'
      ? 'rgba(0, 0, 0, 0.08)'
      : 'rgba(255, 255, 255, 0.1)'
  }`,
  padding: '12px 16px'
}));

const TableHeaderCell = styled(MuiTableCell)(({ theme }) => ({
  fontWeight: 700,
  backgroundColor: theme.palette.mode === 'light' ? '#F5F5F5' : '#2A2A2A',
  borderBottom: `2px solid ${
    theme.palette.mode === 'light'
      ? 'rgba(0, 0, 0, 0.12)'
      : 'rgba(255, 255, 255, 0.12)'
  }`,
  padding: '12px 16px'
}));

const ChargingStationsTab = () => {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingStation, setEditingStation] = useState<ChargingStation | null>(null);
  const [stationName, setStationName] = useState('');

  const { data: stationsData, isLoading } = useGetChargingStationsQuery();
  const [createStation] = useCreateChargingStationMutation();
  const [updateStation] = useUpdateChargingStationMutation();
  const [deleteStation] = useDeleteChargingStationMutation();

  const handleOpenDialog = (station?: ChargingStation) => {
    if (station) {
      setEditingStation(station);
      setStationName(station.name);
    } else {
      setEditingStation(null);
      setStationName('');
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingStation(null);
    setStationName('');
  };

  const handleSave = async () => {
    try {
      if (editingStation) {
        await updateStation({
          id: editingStation._id,
          body: { name: stationName }
        }).unwrap();
      } else {
        await createStation({ name: stationName }).unwrap();
      }
      handleCloseDialog();
    } catch (error) {
      console.error('Failed to save charging station:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm(t('batteries.confirmDeleteStation'))) {
      try {
        await deleteStation(id).unwrap();
      } catch (error) {
        console.error('Failed to delete charging station:', error);
      }
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          {t('batteries.addStationBtn')}
        </Button>
      </Box>

      <TableContainer component={Paper} sx={{ borderRadius: '12px', overflow: 'hidden' }}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : !stationsData?.data || stationsData.data.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <Typography variant="body1" color="textSecondary">
              {t('batteries.noStations')}
            </Typography>
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>{t('batteries.colName')}</TableHeaderCell>
                <TableHeaderCell align="right">{t('batteries.colActions')}</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stationsData.data.map(station => (
                <StyledTableRow key={station._id}>
                  <StyledTableCell>{station.name}</StyledTableCell>
                  <StyledTableCell align="right">
                    <Box display="flex" gap={1} justifyContent="flex-end">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(station)}
                        title={t('batteries.editTooltip')}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(station._id)}
                        title={t('batteries.deleteTooltip')}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  </StyledTableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingStation ? t('batteries.editStationTitle') : t('batteries.addStationTitle')}
        </DialogTitle>
        <DialogContent>
          <Box mt={1}>
            <TextField
              label={t('batteries.stationNameLabel')}
              value={stationName}
              onChange={e => setStationName(e.target.value)}
              fullWidth
              required
              autoFocus
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('batteries.cancelBtn')}</Button>
          <Button onClick={handleSave} variant="contained" disabled={!stationName.trim()}>
            {t('batteries.saveBtn')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChargingStationsTab;
